import { useEffect } from "react";

import * as Sentry from "@sentry/react";
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from "react-router-dom";

import { type Environment, detectEnvironment } from "@/lib/environment";

/**
 * Public client-side Sentry DSN. Browser DSNs are *not* secret — they ship
 * embedded in the bundle by design (Sentry scopes ingestion per project, not
 * by DSN secrecy), so hardcoding it here matches the SPA's existing no-env,
 * hostname-detection convention. The backend uses the same project via the
 * `SENTRY_DSN` env var / Secrets Manager value — a separate delivery path.
 */
const SENTRY_DSN =
  "https://d5e38fba0d84fc37a2bba17280cf54ca@o4511547214856192.ingest.us.sentry.io/4511547643723776";

/** Hostname-detected env label → Sentry `environment` tag. */
const SENTRY_ENVIRONMENT: Record<Environment["label"], string> = {
  Prod: "production",
  Develop: "development",
  Sandbox: "sandbox",
  Local: "local",
};

const { label } = detectEnvironment();

/**
 * Initialize Sentry as a module side effect. Imported first in `main.tsx`
 * so `Sentry.init` runs before `router.tsx` is evaluated — the React Router
 * v7 tracing integration must be registered before the router is created,
 * otherwise the initial pageload transaction is missed.
 */
Sentry.init({
  dsn: SENTRY_DSN,
  environment: SENTRY_ENVIRONMENT[label],
  // Local dev never reports — only deployed environments send events.
  enabled: label !== "Local",
  // Release is injected by @sentry/vite-plugin at build time — the git SHA
  // (SENTRY_RELEASE) in the Docker build, or git auto-detection locally.
  integrations: [
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    // Replay masks all text + inputs by default — appropriate for a B2B
    // surface that renders customer data.
    Sentry.replayIntegration(),
  ],
  // Full performance tracing (mirrors the backend's `traces_sample_rate`).
  tracesSampleRate: 1.0,
  // Sample a slice of all sessions for replay, but always capture a replay
  // when an error fires.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});
